import { TextField, IconButton } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { FC } from 'react'
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { CustomDatePickerControllerProps } from '../../interfaceTypes'
import dayjs from 'dayjs'

export const CustomDatePickerController: FC<CustomDatePickerControllerProps> = ({
  controllerName,
  controllerLabel,
  defaultValue,
  isDisabled,
  readOnly,
  sx,
  onBlur,
  onKeyDown,
  placeholder,
  required = false,
  fullWidth = true,
  setParams,
  minDate,
  maxDate,
  autoFocus,
  format,
  disablePast = false
}): JSX.Element => {
  const { control } = useFormContext()

  const handleChange = (field: ControllerRenderProps<FieldValues, string>) => (value: dayjs.Dayjs | null) => {
    if (!value) {
      field.onChange(null)

      if (setParams) {
        setParams(null, controllerName)
      }
      return
    }

    const date = dayjs(value).format('YYYY-MM-DDTHH:mm:ssZ')

    field.onChange(date)

    if (setParams) {
      setParams(date, controllerName)
    }
  }

  return (
    <Controller
      name={controllerName}
      control={control}
      render={({ field, fieldState: { invalid, error: { message } = {} } }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={controllerLabel}
            value={field.value ?? null}
            disabled={isDisabled}
            inputFormat="MM/DD/YYYY"
            readOnly={readOnly}
            autoFocus={autoFocus}
            onChange={handleChange(field)}
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            disablePast={disablePast}
            renderInput={params => (
              <TextField
                {...params}
                margin="dense"
                fullWidth={fullWidth}
                defaultValue={defaultValue}
                error={invalid}
                helperText={message}
                placeholder={placeholder}
                required={required}
                variant="outlined"
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                sx={sx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {field.value && (
                        <IconButton
                          disabled={isDisabled}
                          onClick={e => {
                            e.stopPropagation()
                            field.onChange(null)
                            if (setParams) {
                              setParams(null, controllerName)
                            }
                          }}
                          size="small"
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </LocalizationProvider>
      )}
    />
  )
}
